import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Calendar, CheckCircle, Archive, Clock } from 'lucide-react';
import { useEvents, useRealTimeEvents } from '../../hooks';
import { useCategories } from '../../hooks/useCategories';
import { EventList } from '../../components/Events';
import Button from '../../components/UI/Button';
import RejectionReasonModal from '../../components/UI/RejectionReasonModal';
import { useAppSelector } from '../../store/hooks';
import eventAPI from '../../services/eventAPI';
import { Event, EventFilters, getEffectiveEventStatus } from '../../types/event';
import { generateEventViewUrl } from '../../utils/urlUtils';
import Toast from '../../components/UI/Toast';
import EnhancedLightCard from '../../components/UI/EnhancedLightCard';
import { useAlert } from '../../contexts';
import { logError } from '../../utils/errorLogger';

const EventsPageImproved: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const { theme } = useAppSelector((state) => state.ui);
  const { showConfirm } = useAlert();
  
  // Toast state for approval/rejection feedback
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [pendingRejection, setPendingRejection] = useState<Event | null>(null);
  
  // Get today's date in YYYY-MM-DD format for default filter (hide past events)
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  
  // Use our custom hook for event management
  const {
    events,
    loading,
    isFiltering,
    error,
    totalCount,
    currentPage,
    totalPages,
    filters,
    setFilters,
    goToPage,
    clearFilters,
    refetch,
  } = useEvents({
    initialFilters: {
      page_size: 9,
      date_from: getTodayDate(), // Default: hide past events
    },
    autoFetch: true
  });

  // Fetch categories for filtering
  const { categories } = useCategories();

  // Real-time updates
  const {
    refreshData,
  } = useRealTimeEvents({
    autoConnect: true,
    onEventUpdate: (event: Event) => {
      refetch(); // Refresh the events list
    },
    onEventCreated: (event: Event) => {
      refetch(); // Refresh the events list
    },
    onEventDeleted: (eventId: string) => {
      refetch(); // Refresh the events list
    },
    onConnectionChange: (connected: boolean) => {
      // Connection status changed
    },
  });

  // Handle refresh when navigating from event creation
  useEffect(() => {
    const state = location.state as any;
    if (state?.refresh && state?.newEventId) {
      // Force refresh the events list (bypass cache)
      (refetch as (forceRefresh?: boolean) => Promise<void>)(true);
      
      // Clear the state so it doesn't refresh again on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state, refetch]);

  const handleCreateEvent = () => {
    const basePath = user?.role === 'admin' ? '/admin' : '/host';
    navigate(`${basePath}/events/create`);
  };

  // Handle event view
  const handleViewEvent = (event: Event) => {
    const viewUrl = generateEventViewUrl(event.id, event.title, event.event_date, user?.role, event.slug);
    
    navigate(viewUrl);
  };

  // Utility function to extract error messages from API responses
  const extractErrorMessage = (error: any, defaultMessage: string): string => {
    if (error.response?.data) {
      const data = error.response.data;
      
      if (data.error?.message) {
        return data.error.message;
      } else if (data.message) {
        return data.message;
      } else if (data.detail) {
        return data.detail;
      } else if (typeof data === 'string') {
        return data;
      }
    } else if (error.message) {
      return error.message;
    }
    
    return defaultMessage;
  };

  // Handle event approval (Admin only)
  const handleApprove = async (event: Event) => {
    if (user?.role !== 'admin') {
      setToastMessage('Only admins can approve events');
      setToastType('error');
      return;
    }

    try {
      await eventAPI.approveEvent(event.id);
      setToastMessage(`Event "${event.title}" has been approved!`);
      setToastType('success');
      refetch(); // Refresh the events list
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to approve event');
      setToastMessage(errorMessage);
      setToastType('error');
    }
  };

  // Handle event rejection (Admin only) - open modal
  const handleReject = (event: Event) => {
    if (user?.role !== 'admin') {
      setToastMessage('Only admins can reject events');
      setToastType('error');
      return;
    }

    setPendingRejection(event);
    setRejectionModalOpen(true);
  };

  // Submit rejection with reason
  const handleRejectSubmit = async (reason: string) => {
    if (!pendingRejection) return;

    setRejectionModalOpen(false);
    
    try {
      await eventAPI.rejectEvent(pendingRejection.id, { reason });
      setToastMessage(`Event "${pendingRejection.title}" has been rejected.`);
      setToastType('success');
      refetch(); // Refresh the events list
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to reject event');
      setToastMessage(errorMessage);
      setToastType('error');
    } finally {
      setPendingRejection(null);
    }
  };

  // Handle event deletion
  const handleDeleteEvent = async (event: Event) => {
    // Confirm deletion with event details
    const confirmMessage = `Are you sure you want to delete "${event.title}"?\n\nEvent Details:\n- Date: ${event.event_date}\n- Location: ${event.location}\n\nThis action cannot be undone.`;
    
    const confirmed = await showConfirm(confirmMessage, undefined, undefined, 'warning', 'Delete Event');
    if (!confirmed) {
      return;
    }

    try {
      // Store current state before deletion

      
      await eventAPI.deleteEvent(event.id);
      setToastMessage(`Event "${event.title}" deleted successfully!`);
      setToastType('success');
      
      // Calculate expected counts after deletion
      const expectedNewCount = Math.max(0, totalCount - 1);
      const expectedNewPages = Math.ceil(expectedNewCount / (filters.page_size || 9));
      
      // If current page will be invalid after deletion, navigate first
      if (currentPage > expectedNewPages && expectedNewPages > 0) {
        goToPage(expectedNewPages);
      } else if (expectedNewPages === 0) {
        goToPage(1);
      } else {
        // Current page will still be valid, just refresh
        await refetch(true);
      }
    } catch (error: any) {
      logError('Delete error', error);
      const errorMessage = extractErrorMessage(error, 'Failed to delete event');
      setToastMessage(errorMessage);
      setToastType('error');
    }
  };

  // Since we now sync status fields in the backend, we can rely on backend filtering
  // and don't need complex frontend filtering
  const getFilteredEvents = () => {
    if (!events) return [];
    
    // Just return the events as they come from the backend
    // The backend API should handle status filtering correctly now
    return events;
  };

  const filteredEvents = getFilteredEvents();
  
  // Ensure pagination values are never invalid
  const safeTotalPages = totalPages > 0 ? totalPages : 0; // Don't force minimum of 1 page when no data
  const safeCurrentPage = Math.min(currentPage, Math.max(1, safeTotalPages)); // Ensure currentPage is never 0

  // Handle filter changes from EventList, including clear all
  const handleFiltersChange = (newFilters: Partial<EventFilters> & { _clearAll?: boolean }) => {
    if (newFilters._clearAll) {
      // Don't preserve ordering when clearing all filters - let it be cleared
      // Only preserve ordering if it was explicitly set to a value in newFilters
      // (which shouldn't happen in clearAll, but handle it just in case)
      const preservedOrdering = newFilters.ordering && newFilters.ordering !== '' ? newFilters.ordering : undefined;
      // Use clearFilters from the hook, which will reset to initial state
      clearFilters();
      // Only restore ordering if it was explicitly provided (shouldn't happen, but safe guard)
      if (preservedOrdering) {
        setFilters({ ordering: preservedOrdering });
      }
      // Otherwise, ordering will be undefined/cleared (no default)
    } else {
      // Special handling for search clearing - force update even if JSON is the same
      const oldSearch = filters.search || '';
      const newSearch = newFilters.search || '';

      // Force update if search is being cleared (from non-empty to empty)
      const forceSearchUpdate = oldSearch !== '' && newSearch === '';

      // Better filter comparison that handles undefined values properly
      const normalizeFilters = (filters: any) => {
        const normalized = { ...filters };
        // Remove undefined values for proper comparison, but preserve the absence of date_from and ordering
        // (date_from: undefined means "show all events", ordering: undefined means "no sort")
        Object.keys(normalized).forEach(key => {
          if (normalized[key] === undefined && key !== 'date_from' && key !== 'ordering') {
            delete normalized[key];
          }
        });
        // If date_from is explicitly undefined, remove it to show all events
        if (normalized.date_from === undefined) {
          delete normalized.date_from;
        }
        // If ordering is explicitly undefined, remove it to clear sort
        if (normalized.ordering === undefined) {
          delete normalized.ordering;
        }
        return normalized;
      };

      const normalizedOldFilters = normalizeFilters(filters);
      const normalizedNewFilters = normalizeFilters(newFilters);
      const hasChanged = JSON.stringify(normalizedOldFilters) !== JSON.stringify(normalizedNewFilters);

      if (hasChanged || forceSearchUpdate) {
        // Prepare filters to set
        // The useEvents hook will handle undefined values (date_from, ordering) and remove them
        // No need to explicitly check since we're passing the filters as-is
        setFilters(newFilters);
        
        // Check if status filter was cleared (All statuses selected)
        const statusWasCleared = filters?.status && !newFilters?.status;
        if (statusWasCleared) {
          // Small delay to ensure filter state is updated
          setTimeout(() => {
            refetch();
          }, 150);
        }
      }
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Events
                </h1>
                <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  {user?.role === 'admin' 
                    ? 'Manage all events in the system' 
                    : 'Manage your events and track guest responses'
                  }
                </p>
              </div>
              <div className="mt-4 sm:mt-0 flex items-center gap-3">
                <Button
                  onClick={handleCreateEvent}
                  leftIcon={<Plus className="h-4 w-4" />}
                >
                  Create New Event
                </Button>
              </div>
            </div>
          </div>

          {/* Stats Cards — aligned with Admin/Host/Guest dashboard */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto px-4 sm:px-6 mb-6">
            <EnhancedLightCard
              title="Active Events"
              value={`${events.filter(e => getEffectiveEventStatus(e) === 'active').length}${totalCount > 0 ? ` / ${totalCount}` : ''}`}
              icon={<CheckCircle />}
              compactHorizontal
            />
            <EnhancedLightCard
              title="Pending Approval"
              value={events.filter(e => getEffectiveEventStatus(e) === 'pending').length}
              icon={<Clock />}
              compactHorizontal
            />
            <EnhancedLightCard
              title="Archived Events"
              value={events.filter(e => e.status === 'archived').length}
              icon={<Archive />}
              compactHorizontal
            />
          </div>

          {/* EventList Component - Handles its own card layout */}
          <EventList
                events={filteredEvents}
                categories={categories}
                loading={loading}
                isFiltering={isFiltering}
                error={error || undefined}
                totalCount={totalCount}
                currentPage={safeCurrentPage}
                totalPages={safeTotalPages}
                onPageChange={goToPage}
                onFiltersChange={handleFiltersChange}
                onEventView={handleViewEvent}
                onEventEdit={(event) => {
                  // Handle event edit
                }}
                onEventDelete={handleDeleteEvent}
                onEventCreate={handleCreateEvent}
                onApprove={handleApprove}
                onReject={handleReject}
                userRole={user?.role}
                showCreateButton={false} // We have it in the header
                viewMode="grid"
                externalFilters={filters} // Pass current filters to EventList
                onForceRefresh={() => refreshData('events')} // Pass force refresh function
          />

      {/* Toast for approval/rejection feedback */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-[120] max-w-md">
          <Toast
            message={toastMessage}
            type={toastType}
            onClose={() => setToastMessage(null)}
          />
        </div>
      )}

      {/* Rejection Reason Modal */}
      <RejectionReasonModal
        isOpen={rejectionModalOpen}
        onClose={() => {
          setRejectionModalOpen(false);
          setPendingRejection(null);
        }}
        onSubmit={handleRejectSubmit}
        eventTitle={pendingRejection?.title}
      />
    </div>
  );
};

export default EventsPageImproved;
